import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ConfirmDialog from '../../../../components/ConfirmDialog/ConfirmDialog';
import { ImportAdminProspectsAPI } from '../../../../api/AdminAPI';
import { CATEGORY_OPTIONS, SELECT_ABOVE_DIALOG } from '../prospectOptions';
import { readFollowersFile } from '../followersFile';
import FollowerRow from './FollowerRow';

/** Limite por chamada de admin/prospects/import.php */
const CHUNK = 500;
/** Lista longa: só as primeiras aparecem, o resto entra igual */
const VISIBLE = 300;

/**
 * Manda os perfis em partes e soma o resultado.
 * @param {Object} base { source, category }
 * @param {Array} items
 * @returns {Promise<{ items: Array, skipped: number }>}
 */
function importInChunks(base, items) {
  const parts = [];
  for (let i = 0; i < items.length; i += CHUNK) parts.push(items.slice(i, i + CHUNK));
  return parts.reduce((previous, part) => previous.then((total) => new Promise((resolve, reject) => {
    ImportAdminProspectsAPI({ ...base, items: part }, (response) => {
      if (!response?.success) {
        reject(new Error(response?.error || 'Não foi possível adicionar.'));
        return;
      }
      resolve({ items: [...total.items, ...(response.items || [])], skipped: total.skipped + (response.skipped || 0) });
    });
  })), Promise.resolve({ items: [], skipped: 0 }));
}

/**
 * ProspectFollowersDialog - Importa uma lista de seguidores do Instagram (CSV ou planilha).
 * A categoria vem do que o perfil diz de si; quem não parece profissional fica de fora por padrão.
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {Function} props.onImported recebe (contatos importados, quantos foram pulados)
 * @param {Function} props.onCancel
 */
function ProspectFollowersDialog({ isOpen, onImported, onCancel }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [origin, setOrigin] = useState('');
  const [fallbackCategory, setFallbackCategory] = useState('other');
  const [onlyProfessionals, setOnlyProfessionals] = useState(true);
  const [unchecked, setUnchecked] = useState([]);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setRows([]);
    setFileName('');
    setUnchecked([]);
    setError('');
  }, [isOpen]);

  const visibleRows = useMemo(
    () => (onlyProfessionals ? rows.filter((row) => row.category) : rows),
    [rows, onlyProfessionals],
  );
  const selected = visibleRows.filter((row) => !unchecked.includes(row.username));
  const professionals = rows.filter((row) => row.category).length;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    try {
      const list = await readFollowersFile(file);
      setRows(list);
      setFileName(file.name);
      setUnchecked([]);
      if (list.length === 0) setError('O arquivo não tem nenhum perfil.');
    } catch (err) {
      setRows([]);
      setError(err.message || 'Não consegui ler o arquivo.');
    }
  };

  const toggle = (username) => setUnchecked((prev) => (
    prev.includes(username) ? prev.filter((u) => u !== username) : [...prev, username]
  ));

  const handleConfirm = () => {
    const from = origin.trim().replace(/^@/, '');
    const items = selected.map((row) => {
      const phone = row.phone.replace(/\D+/g, '');
      return {
        source_ref: row.username.toLowerCase(),
        name: row.name || row.username,
        instagram: row.username,
        email: row.email,
        whatsapp: phone.length >= 10 ? phone : '',
        category: row.category || fallbackCategory,
        note: [from && `Seguidor de @${from}`, row.bio && `Bio: ${row.bio}`].filter(Boolean).join('\n'),
      };
    });

    setImporting(true);
    setError('');
    importInChunks({ source: 'instagram', category: fallbackCategory }, items)
      .then((result) => onImported(result.items, result.skipped))
      .catch((err) => setError(err.message))
      .finally(() => setImporting(false));
  };

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title="Importar seguidores do Instagram"
      variant="info"
      confirmLabel={selected.length ? `Adicionar ${selected.length} ao catálogo` : 'Adicionar ao catálogo'}
      cancelLabel="Fechar"
      onConfirm={handleConfirm}
      onCancel={onCancel}
      confirmDisabled={importing || selected.length === 0}
      loading={importing}
      maxWidth={760}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'auto 1fr 1fr' }, gap: 1.5, alignItems: 'center' }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileOutlinedIcon fontSize="small" />} disabled={importing} sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 600 }}>
            Escolher arquivo
            <input hidden type="file" accept=".csv,.xlsx,.xls" onChange={handleFile} />
          </Button>
          <TextField size="small" label="Seguidores de" placeholder="@concorrente" value={origin} onChange={(e) => setOrigin(e.target.value)} disabled={importing} />
          <TextField size="small" select label="Sem categoria, usar" value={fallbackCategory} onChange={(e) => setFallbackCategory(e.target.value)} disabled={importing} SelectProps={SELECT_ABOVE_DIALOG}>
            {CATEGORY_OPTIONS.map((item) => <MenuItem key={item.value} value={item.value}>{item.label}</MenuItem>)}
          </TextField>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        {rows.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {fileName}: {rows.length} perfis, {professionals} parecem profissionais pelo @, nome ou bio.
            </Typography>
            <FormControlLabel
              control={<Switch size="small" checked={onlyProfessionals} onChange={(e) => setOnlyProfessionals(e.target.checked)} />}
              label={<Typography variant="body2">Só quem parece profissional</Typography>}
            />
          </Box>
        )}

        <Box sx={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {visibleRows.slice(0, VISIBLE).map((row) => (
            <FollowerRow key={row.username} item={row} checked={!unchecked.includes(row.username)} onToggle={toggle} />
          ))}
          {visibleRows.length > VISIBLE && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1, textAlign: 'center' }}>
              Mostrando {VISIBLE} de {visibleRows.length}; os outros entram também.
            </Typography>
          )}
        </Box>
      </Box>
    </ConfirmDialog>
  );
}

export default ProspectFollowersDialog;
